/**
 * Environment Variables Validation
 * Checks all required environment variables at app startup
 */

interface EnvConfig {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_PUBLISHABLE_KEY: string;
  VITE_SUPABASE_PROJECT_ID: string;
}

const REQUIRED_ENV_VARS: (keyof EnvConfig)[] = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_PUBLISHABLE_KEY',
  'VITE_SUPABASE_PROJECT_ID',
];

export function validateEnvironmentVariables(): void {
  const missing: string[] = [];
  const invalid: string[] = [];

  REQUIRED_ENV_VARS.forEach((varName) => {
    const value = import.meta.env[varName];
    
    if (!value) {
      missing.push(varName);
    } else if (typeof value !== 'string' || value.trim() === '') {
      invalid.push(varName);
    }
  });

  if (missing.length > 0 || invalid.length > 0) {
    const errorMessages: string[] = [
      '❌ ENVIRONMENT CONFIGURATION ERROR',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
    ];

    if (missing.length > 0) {
      errorMessages.push(
        '',
        '🚫 Missing required environment variables:',
        ...missing.map(v => `   - ${v}`)
      );
    }

    if (invalid.length > 0) {
      errorMessages.push(
        '',
        '⚠️  Invalid environment variables (empty or malformed):',
        ...invalid.map(v => `   - ${v}`)
      );
    }

    errorMessages.push(
      '',
      '📋 How to fix:',
      '   1. Copy .env.example to .env',
      '   2. Fill in all required values',
      '   3. Restart the development server',
      '',
      '💡 Get your Supabase credentials from:',
      '   https://app.supabase.com/project/_/settings/api',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
    );

    console.error(errorMessages.join('\n'));

    // In development, show alert
    if (import.meta.env.DEV) {
      alert(
        'Environment Configuration Error!\n\n' +
        'Missing required environment variables.\n' +
        'Check the browser console for details.'
      );
    }

    throw new Error('Missing or invalid environment variables. Check console for details.');
  }

  // Success message in development
  if (import.meta.env.DEV) {
    console.log(
      '%c✅ Environment Variables Validated',
      'color: #10b981; font-weight: bold; font-size: 14px;'
    );
    console.log(
      '%cAll required environment variables are present and valid.',
      'color: #6b7280; font-size: 12px;'
    );
  }
}

export function getEnvVar(key: keyof EnvConfig): string {
  const value = import.meta.env[key];
  if (!value) {
    console.error(`Environment variable ${key} is not defined`);
    return '';
  }
  return value;
}
